import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from "@mui/material";
import { tokens } from "../../../theme";
import Loader from '../../../components/Utility/Loader';
import { Button as DSButton } from "../../../components/DesignSystem/components/Button";
import { Heading, Text } from "../../../components/DesignSystem/components/Typography";
import { Input } from "../../../components/DesignSystem/components/Input";
import { Check, Mail, ArrowRight, Lock, ShieldCheck, ArrowLeft, Send } from "lucide-react";
import { Logo } from "../../../components/DesignSystem/components/Logo";

import './EmailVerify.css';
import signupBg from '../../../assets/images/img_signup.png';

import * as userActions from "../../../actions/user";
import * as authActions from "../../../actions/auth";

// Email validation utility
const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

export const EmailVerify = () => {
    const navigate = useNavigate();

    const [email, setEmail] = useState('');
    const [existUser, setExistUser] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Smooth Transition Success Overlay State
    const [showSuccessOverlay, setShowSuccessOverlay] = useState(true);
    const [isEnteringOverlay, setIsEnteringOverlay] = useState(false);
    const [isLeavingOverlay, setIsLeavingOverlay] = useState(false);
    
    // Support for verification flow
    const [verificationSent, setVerificationSent] = useState(false);
    const [isVerified, setIsVerified] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);
    const pollInterval = useRef(null);
    const [testUrl, setTestUrl] = useState('');

    const theme = useTheme();
    const mode = theme.palette.mode;
    
    // Success Dialog Timeout
    useEffect(() => {
        // Trigger Fade In slightly after mount
        const entryTimer = setTimeout(() => {
            setIsEnteringOverlay(true);
        }, 100);

        // Start Fade Out after 3 seconds of visibility
        const leaveTimer = setTimeout(() => {
            setIsLeavingOverlay(true);
        }, 3000);
        
        // Final unmount after fade out duration
        const unmountTimer = setTimeout(() => {
            setShowSuccessOverlay(false);
        }, 3800);

        return () => {
            clearTimeout(entryTimer);
            clearTimeout(leaveTimer);
            clearTimeout(unmountTimer);
        };
    }, []);
    
    // Track Tailwind loading state to prevent FOUC (Flash of Unstyled Content)
    const [isTailwindLoaded, setIsTailwindLoaded] = useState(!!window.tailwind);

    useEffect(() => {
        const applyConfig = () => {
            const modeColors = tokens(mode);
            if (window.tailwind) {
                window.tailwind.config = {
                    darkMode: "class",
                    theme: {
                        extend: {
                            colors: {
                                "primary": modeColors.greenAccent[500] || "#ff794d",
                                "brand": modeColors.greenAccent[500] || "#ff794d",
                                "background-light": mode === "dark" ? modeColors.primary[500] : "#f8f6f5",
                                "background-dark": modeColors.primary[500] || "#23140f",
                                "premium-black": mode === "dark" ? "#ffffff" : "#111111",
                                "action-black": mode === "dark" ? "#ffffff" : "#111111"
                            },
                            fontFamily: {
                                "display": ["Inter", "sans-serif"],
                                "body": ['"Source Sans Pro"', "sans-serif"],
                                "button": ["Poppins", "sans-serif"]
                            },
                            borderRadius: {
                                "DEFAULT": "1rem",
                                "lg": "2rem",
                                "xl": "3rem",
                                "full": "9999px"
                            },
                        },
                    },
                };
            }
        };

        if (!document.getElementById("tailwind-script")) {
            const script = document.createElement("script");
            script.id = "tailwind-script";
            script.src = "https://cdn.tailwindcss.com?plugins=forms,container-queries";
            
            script.onload = () => {
                applyConfig();
                setIsTailwindLoaded(true);
            };

            document.head.appendChild(script);
            
            const link1 = document.createElement("link");
            link1.rel = "stylesheet";
            link1.href = "https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:wght@100..700,0..1&display=swap";
            document.head.appendChild(link1);
        } else {
            applyConfig();
            if (window.tailwind) setIsTailwindLoaded(true);
        }
    }, [mode]);

    // Single validation function for email checks
    const validateEmails = useCallback(() => {
        const errors = [];

        // Check 1: Email is valid
        if (!email) {
            errors.push('Email is required.');
        } else if (!validateEmail(email)) {
            errors.push('Please enter a valid email address.');
        }

        return errors;
    }, [email]);

    // Check verification status
    const checkStatus = useCallback(async () => {
        try {
            const response = await authActions.checkEmailVerification();
            console.log("🔍 Polling checkEmailVerification response:", response);
            // Using loose truthy check to handle boolean true, 1, or "true" from server
            if (response && response.status && (response.emailVerified === true || response.emailVerified == 1 || response.emailVerified === 'true')) {
                setIsVerified(true);
                // Navigation will be handled by the specialized useEffect
                return true;
            }
            return false;
        } catch (e) {
            return false;
        }
    }, [setIsVerified]); // Removed navigate from deps as it's handled by useEffect now

    // Fetch user email on component mount
    useEffect(() => {
        const fetchUserEmail = async () => {
            try {
                setLoading(true);
                const response = await userActions.getEmailDetail();
                console.log("🔍 Mount getEmailDetail response:", response);

                if (response && response.email) {
                    setExistUser(true);
                    setEmail(response.email);
                    
                    await checkStatus();
                    // Removed automatic setVerificationSent(true) to avoid flickering
                    // checkStatus will handle navigation if already verified.
                    // If not verified, we stay on the input screen (form) until Verify is clicked.
                }
            } catch (error) {
                console.error("Error fetching user email:", error);
                setErrorMessage('Failed to load user email. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        fetchUserEmail();
        
        return () => {
            if (pollInterval.current) clearInterval(pollInterval.current);
        };
    }, [checkStatus]);

    // Polling for verification status when screen is "Sent"
    useEffect(() => {
        if (verificationSent && !isVerified) {
            pollInterval.current = setInterval(checkStatus, 5000);
        } else {
            if (pollInterval.current) clearInterval(pollInterval.current);
        }
        return () => {
            if (pollInterval.current) clearInterval(pollInterval.current);
        };
    }, [verificationSent, isVerified, checkStatus]);

    // Specialized Navigation Effect: Triggered whenever isVerified changes
    useEffect(() => {
        if (isVerified) {
            console.log("Verification confirmed, navigating to /abtyourself...");
            navigate("/abtyourself");
        }
    }, [isVerified, navigate]);

    // Resend cooldown timer
    useEffect(() => {
        if (resendCooldown > 0) {
            const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [resendCooldown]);

    // Handle email input change
    const handleEmailChange = useCallback((e) => {
        setEmail(e.target.value);
        setErrorMessage(''); // Clear error when user types
    }, []);

    const handleResend = async () => {
        if (resendCooldown > 0) return;
        
        setIsSubmitting(true);
        try {
            const result = await authActions.sendVerificationEmail();
            if (result && result.status) {
                setResendCooldown(60);
                if (!process.env.NODE_ENV || process.env.NODE_ENV !== 'production') {
                    if (result.url) {
                        setTestUrl(result.url);
                        console.log("🛠️ Test URL available:", result.url);
                    }
                }
            } else {
                setErrorMessage(result?.message || 'Failed to resend email.');
            }
        } catch (error) {
            setErrorMessage('Error resending email. Please try again later.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle form submission
    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();

        if (isSubmitting) return;

        setErrorMessage('');

        const errors = validateEmails();
        if (errors.length > 0) {
            setErrorMessage(errors[0]); // Show first error
            return;
        }

        setIsSubmitting(true);

        try {
            if (!existUser) {
                await userActions.updateEmail(JSON.stringify({ email: email.trim() }));
            }
            
            // Send verification
            const result = await authActions.sendVerificationEmail();
            console.log("🚀 sendVerificationEmail (Verify Button) response:", result);
            
            if (result && result.status) {
                // Loose check for already verified
                if (result.emailVerified === true || result.emailVerified == 1 || result.emailVerified === 'true') {
                    // Scenario A: User is already verified (happens in Dev mode)
                    setIsVerified(true);
                    setVerificationSent(false); 
                    // Navigation handled by useEffect
                } else {
                    // Scenario B: Standard production, show "Check Inbox" screen
                    setVerificationSent(true);
                    setResendCooldown(60);
                    
                    if (!process.env.NODE_ENV || process.env.NODE_ENV !== 'production') {
                        if (result.url) {
                            setTestUrl(result.url);
                        }
                    }
                }
            } else {
                setErrorMessage(result?.message || 'Failed to send verification email.');
            }
        } catch (error) {
            console.error("Error setting email:", error);
            setErrorMessage(`*${error.message || 'An error occurred. Please try again later.'}`);
        } finally {
            setIsSubmitting(false);
        }
    }, [validateEmails, existUser, email, isSubmitting, setIsVerified]);

    const handleSkip = useCallback(() => {
        navigate("/abtyourself");
    }, [navigate]);

    const handleBackContext = () => {
        if (verificationSent && !existUser) {
            setVerificationSent(false); // Let them fix email
        } else {
            window.history.back();
        }
    };

    return (
        <div 
            className={`${mode === "dark" ? "dark" : ""} bg-background-light dark:bg-background-dark font-display text-slate-900 dark:text-slate-100 antialiased overflow-hidden w-full m-0 p-0`}
            style={{ height: "100vh", opacity: isTailwindLoaded ? 1 : 0, transition: 'opacity 0.1s ease-out' }}
        >
            {loading && <Loader />}
            <div className="flex h-screen w-full">
                
                {/* Left Side: Visual/Branding */}
                <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-brand/10 h-full">
                    <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${signupBg})` }}></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-background-dark/60 to-transparent"></div>
                    <div className="absolute bottom-12 left-12 right-12 text-white">
                        <div className="login-hero-header">
                                                    <Logo showText={false} />
                                                    <h2 className="login-hero-title">myTamilDate</h2>
                                                </div>
                    <Text as="p" size="lg" className="mt-4 text-white/80 max-w-md">Join over 2 million people finding their perfect match every single day.</Text>
                    </div>
                </div>
                
                {/* Right Side: Content */}
                <div className="w-full lg:w-1/2 flex flex-col bg-white dark:bg-background-dark px-8 lg:px-20 pt-[var(--ds-space-mobile-top)] pb-12 lg:py-16 h-full overflow-y-auto relative">
                    
                    {/* Phone Verification Success Overlay */}
                    {showSuccessOverlay && (
                        <div className={`absolute inset-0 z-50 flex flex-col items-center justify-center bg-white dark:bg-background-dark transition-all duration-1000 ease-in-out ${!isEnteringOverlay || isLeavingOverlay ? 'opacity-0 backdrop-blur-none' : 'opacity-100 backdrop-blur-sm'}`}>
                            <div className={`flex flex-col items-center px-8 text-center transition-all duration-1000 cubic-bezier(0.34, 1.56, 0.64, 1) ${!isEnteringOverlay ? 'scale-75 opacity-0 translate-y-12' : isLeavingOverlay ? 'scale-90 opacity-0 -translate-y-8' : 'scale-100 opacity-100 translate-y-0'}`}>
                                <div className="w-24 h-24 lg:w-28 lg:h-28 bg-[#ff794d]/10 rounded-full flex items-center justify-center mb-8 animate-bounce transition-transform duration-1000">
                                    <ShieldCheck className="text-[#ff794d] lg:scale-125 transition-transform" size={48} strokeWidth={2.5} />
                                </div>
                                <h2 className="text-3xl lg:text-5xl font-black font-display tracking-tight text-slate-900 dark:text-white mb-3 text-center drop-shadow-sm leading-tight">Phone Number Verified!</h2>
                                <p className="text-slate-500 dark:text-slate-400 font-semibold text-lg lg:text-xl tracking-tight leading-relaxed max-w-xs mx-auto">Your phone is now securely linked.</p>
                            </div>
                        </div>
                    )}

                    <div className={`flex-1 flex flex-col max-w-md mx-auto w-full transition-all duration-1000 ease-in-out ${showSuccessOverlay && !isLeavingOverlay ? 'opacity-0 translate-y-12 scale-105' : 'opacity-100 translate-y-0 scale-100'}`}>
                        
                        {/* Content Wrapper for Responsive Vertical Centering */}
                        <div className="flex-1 flex flex-col justify-start lg:justify-center w-full lg:my-auto pb-8 lg:py-8">
                            {/* Mobile Back Button */}
                            <div className="lg:hidden flex justify-start mb-[var(--ds-space-8)] -ml-2 text-slate-900 dark:text-slate-100">
                                <button type="button" onClick={handleBackContext} className="flex items-center justify-center w-10 h-10 rounded-full bg-transparent hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors" aria-label="Go back">
                                    <ArrowLeft className="shrink-0" size={24} />
                                </button>
                            </div>
                                                    
                            {/* Main Card Dynamic View */}
                            <div className="pb-[var(--ds-space-8)] text-left">
                                {!verificationSent && !isVerified ? (
                                    <>
                                        <div className="flex items-center gap-[var(--ds-space-2)] mb-[var(--ds-space-6)] animate-in fade-in slide-in-from-bottom-2">
                                            <div className="flex items-center justify-center h-6 w-6 bg-brand/10 rounded-full">
                                                <Check className="text-brand" size={14} strokeWidth={3} color='var(--ds-primary)'/>
                                            </div>
                                            <span className="text-sm font-semibold ds-text-primary">Phone Number Verified</span>
                                        </div>
                                        <h1 className="text-slate-900 dark:text-slate-100 text-4xl lg:text-5xl font-black font-display tracking-tight mb-4 animate-in fade-in slide-in-from-bottom-3">
                                            Add your email
                                        </h1>
                                        <p className="text-slate-500 dark:text-slate-400 text-lg leading-normal mb-[var(--ds-space-8)] animate-in fade-in slide-in-from-bottom-4">
                                            So you never lose access to your account and stay updated on matches.
                                        </p>
                                        
                                        <form onSubmit={handleSubmit} className="flex flex-col gap-[var(--ds-space-8)] animate-in fade-in slide-in-from-bottom-5">
                                            <div className="w-full">
                                                <Input
                                                    labelClassName="ds-label-login"
                                                    label="EMAIL ADDRESS"
                                                    icon="mail"
                                                    id="email"
                                                    placeholder="Enter your email address"
                                                    type="email"
                                                    value={email}
                                                    onChange={handleEmailChange}
                                                    readOnly={existUser}
                                                    disabled={loading || isSubmitting}
                                                    className={errorMessage ? 'border-red-500' : ''}
                                                />
                                                {errorMessage && (
                                                    <Text as="p" size="sm" weight="medium" className="text-red-500 mt-1 ml-4">{errorMessage}</Text>
                                                )}
                                            </div>
                                            
                                            <DSButton 
                                                type="submit"
                                                variant="cta"
                                                disabled={loading || isSubmitting}
                                                style={{ backgroundColor: mode === "dark" ? "#ffffff" : "#111111", color: mode === "dark" ? "#111111" : "#ffffff" }}                                                
                                            >
                                                {isSubmitting ? 'Sending Link...' : 'Verify'}
                                            </DSButton>
                                        </form>
                                        
                                        <div className="mt-[var(--ds-space-12)] flex flex-col items-center gap-[var(--ds-space-4)] animate-in fade-in slide-in-from-bottom-6">
                                            <DSButton variant="ghost" className="text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 transition-colors font-medium border-0 text-base" onClick={handleSkip} type="button">
                                                Skip for now
                                            </DSButton>
                                            <Text as="p" size="xs" weight="medium" className="uppercase tracking-[0.1em] text-slate-400">You can add it later in Settings</Text>
                                        </div>
                                    </>
                                ) : !isVerified ? (
                                    <>
                                        <div className="flex flex-col items-center justify-center text-center py-4 animate-in fade-in slide-in-from-bottom-4 zoom-in-95">
                                            <div className="w-24 h-24 bg-brand/10 rounded-full flex items-center justify-center mb-8 relative">
                                                <div className="absolute inset-0 bg-brand/20 rounded-full animate-ping opacity-25"></div>
                                                <Mail size={40} className="text-brand relative z-10" />
                                            </div>
                                            
                                            <h2 className="text-slate-900 dark:text-slate-100 text-3xl lg:text-4xl font-black font-display tracking-tight mb-4">
                                                Check your inbox!
                                            </h2>
                                            
                                            <p className="text-slate-500 dark:text-slate-400 text-lg leading-relaxed mb-8 max-w-md mx-auto">
                                                We've sent a magic verification link to <br/>
                                                <strong className="text-slate-900 dark:text-white mt-1 block">{email}</strong>
                                                <span className="block mt-4 text-base">Please tap the link inside the email to instantly verify your account.</span>
                                            </p>

                                            {errorMessage && (
                                                <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-xl mb-6 text-sm font-medium w-full">
                                                    {errorMessage}
                                                </div>
                                            )}
                                            
                                            <div className="w-full flex flex-col gap-4">
                                                <DSButton 
                                                    onClick={handleResend}
                                                    disabled={isSubmitting || resendCooldown > 0}
                                                    variant="secondary"
                                                    className="w-full"
                                                >
                                                    {isSubmitting ? 'Sending...' : 
                                                     resendCooldown > 0 ? `Resend Link (${resendCooldown}s)` : 'Resend Link'}
                                                </DSButton>

                                                {!existUser && (
                                                    <DSButton 
                                                        onClick={() => setVerificationSent(false)}
                                                        variant="ghost"
                                                        className="w-full text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 border-0"
                                                    >
                                                        Change Email Address
                                                    </DSButton>
                                                )}
                                            </div>

                                            <div className="mt-8 flex items-center justify-center gap-3 w-full bg-slate-50 dark:bg-slate-800/40 p-4 rounded-2xl border border-slate-100 dark:border-slate-800">
                                                <div className="w-4 h-4 rounded-full border-2 border-brand border-t-transparent animate-spin shrink-0"></div>
                                                <span className="text-sm font-semibold text-slate-600 dark:text-slate-300">
                                                    Waiting for verification...
                                                </span>
                                            </div>
                                        </div>
                                    </>
                                ) : null}
                            </div>
                        </div>

                        {/* Trust Indicators placed at bottom via mt-auto */}
                        <div className="mt-auto pt-[var(--ds-space-8)] flex justify-center items-center gap-[var(--ds-space-8)] opacity-40 grayscale border-t border-slate-200 dark:border-slate-800">
                            <div className="flex items-center gap-[var(--ds-space-2)]">
                                <Lock size={16} />
                                <span className="text-xs font-semibold uppercase tracking-[0.1em]">Secure SSL</span>
                            </div>
                            <div className="flex items-center gap-[var(--ds-space-2)]">
                                <ShieldCheck size={16} />
                                <span className="text-xs font-semibold uppercase tracking-[0.1em]">Data Privacy</span>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

